import { haptic } from '../utils/haptic';
import { IconRocket, IconSparkles, IconSearch, IconMail } from './icons';
import type { ReactNode } from 'react';

type EmptyStateType = 'showcase' | 'requests' | 'my-projects' | 'my-requests' | 'search';

interface EmptyStateProps {
  type: EmptyStateType;
  onAction?: () => void;
}

const CONFIG: Record<EmptyStateType, {
  icon: ReactNode;
  title: string;
  subtitle: string;
  actionLabel?: string;
  gradient: string;
}> = {
  showcase: {
    icon: <IconRocket size={40} />,
    title: 'Пока нет опубликованных проектов.',
    subtitle: 'Создайте свой проект и станьте первым.',
    actionLabel: 'Создать проект',
    gradient: 'linear-gradient(135deg, rgba(120,80,255,0.25), rgba(244,114,182,0.2))',
  },
  requests: {
    icon: <IconMail size={40} />,
    title: 'Пока нет входящих заявок.',
    subtitle: 'Опубликуйте проект, чтобы получать отклики.',
    gradient: 'linear-gradient(135deg, rgba(120,80,255,0.25), rgba(196,181,253,0.18))',
  },
  'my-projects': {
    icon: <IconSparkles size={40} />,
    title: 'У вас пока нет проектов.',
    subtitle: 'Создайте первый проект и покажите его миру.',
    actionLabel: 'Создать проект',
    gradient: 'linear-gradient(135deg, rgba(120,80,255,0.25), rgba(168,85,247,0.2))',
  },
  'my-requests': {
    icon: <IconSearch size={40} />,
    title: 'Нет заявок',
    subtitle: 'Создай заявку на разработку\nили поиск партнёра',
    actionLabel: 'Создать заявку',
    gradient: 'linear-gradient(135deg, rgba(244,114,182,0.2), rgba(120,80,255,0.2))',
  },
  search: {
    icon: <IconSearch size={40} />,
    title: 'Ничего не найдено',
    subtitle: 'Попробуй изменить запрос\nили сбросить фильтры',
    actionLabel: 'Сбросить фильтры',
    gradient: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.08))',
  },
};

export function EmptyState({ type, onAction }: EmptyStateProps) {
  const c = CONFIG[type];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 24px',
      textAlign: 'center',
    }}>
      {/* Animated icon container */}
      <div
        className="animate-float"
        style={{
          position: 'relative',
          width: 88,
          height: 88,
          borderRadius: 24,
          background: c.gradient,
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 20,
        }}
      >
        {c.icon}
        {/* Glow behind */}
        <div style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 24,
          background: c.gradient,
          filter: 'blur(20px)',
          opacity: 0.5,
          zIndex: -1,
        }} />
      </div>

      <h3 style={{
        fontSize: '1rem',
        fontWeight: 700,
        color: 'rgba(255,255,255,0.9)',
        marginBottom: 8,
      }}>
        {c.title}
      </h3>

      <p style={{
        fontSize: '0.875rem',
        color: 'rgba(255,255,255,0.4)',
        whiteSpace: 'pre-line',
        lineHeight: 1.6,
        marginBottom: onAction && c.actionLabel ? 28 : 0,
        maxWidth: 260,
      }}>
        {c.subtitle}
      </p>

      {onAction && c.actionLabel && (
        <button
          type="button"
          className="btn btn-primary animate-pulse-glow"
          onClick={() => {
            haptic.tap();
            onAction();
          }}
          style={{
            padding: '12px 24px',
            borderRadius: 12,
            fontSize: '0.9375rem',
            fontWeight: 600,
          }}
        >
          {c.actionLabel}
        </button>
      )}
    </div>
  );
}
